import { Image as ExpoImage, type ImageProps } from 'expo-image';
import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors } from '@/src/theme/colors';

type LoadableImageProps = Omit<ImageProps, 'style'> & {
  loaderColor?: string;
  loaderDelayMs?: number;
  style: StyleProp<ViewStyle>;
};

function imageSourceKey(source: ImageProps['source']): string {
  if (typeof source === 'number' || typeof source === 'string') {
    return String(source);
  }

  if (Array.isArray(source)) {
    return source.map((item) => imageSourceKey(item)).join('|');
  }

  if (source && typeof source === 'object' && 'uri' in source) {
    return source.uri ?? '';
  }

  return '';
}

export function LoadableImage({
  loaderColor = colors.primary,
  loaderDelayMs = 350,
  onError,
  onLoad,
  onLoadStart,
  source,
  style,
  ...props
}: LoadableImageProps) {
  const sourceKey = useMemo(() => imageSourceKey(source), [source]);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    setLoading(true);
    setShowLoader(false);

    const timeout = setTimeout(() => {
      setShowLoader(true);
    }, loaderDelayMs);

    return () => clearTimeout(timeout);
  }, [loaderDelayMs, sourceKey]);

  function finishLoading() {
    setLoading(false);
    setShowLoader(false);
  }

  return (
    <View style={[style, styles.root]}>
      <ExpoImage
        {...props}
        onError={(event) => {
          finishLoading();
          onError?.(event);
        }}
        onLoad={(event) => {
          finishLoading();
          onLoad?.(event);
        }}
        onLoadStart={() => {
          setLoading(true);
          onLoadStart?.();
        }}
        source={source}
        style={styles.image}
      />
      {loading && showLoader ? (
        <View pointerEvents="none" style={styles.loaderOverlay}>
          <ActivityIndicator color={loaderColor} />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  image: {
    height: '100%',
    width: '100%',
  },
  loaderOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
  },
  root: {
    overflow: 'hidden',
  },
});
